import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';

const BETA_FREE_MODE = process.env.REACT_APP_BETA_FREE_MODE === 'true';
const BETA_BANNER_HIDE_KEY = 'np_beta_banner_hidden_until';
const HIDE_DURATION_MS = 24 * 60 * 60 * 1000;

const Wrapper = styled.div`
  width: 100%;
  background: #fffbeb;
  border-bottom: 1px solid #fcd34d;
  box-shadow: 0 1px 0 rgba(146, 64, 14, 0.08);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1600;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.55rem 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: #78350f;
  font-family: 'Roboto', sans-serif;

  @media (max-width: 1024px) {
    padding: 0.5rem 0.7rem;
    gap: 0.45rem;
  }
`;

const TextWrap = styled.div`
  min-width: 0;
  flex: 1;
`;

const Text = styled.p`
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.25;
  color: #78350f;
  overflow-wrap: anywhere;

  @media (max-width: 1024px) {
    font-size: 0.8rem;
  }
`;

const MobileText = styled(Text)`
  display: none;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const DesktopText = styled(Text)`
  display: block;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: #f59e0b;
  color: #ffffff;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
`;

const Action = styled(Link)`
  flex-shrink: 0;
  color: #7c2d12;
  background: #fde68a;
  border: 1px solid #f5c45d;
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: #fcd34d;
    color: #78350f;
  }

  @media (max-width: 1024px) {
    padding: 0.3rem 0.52rem;
    font-size: 0.76rem;
  }
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  border: 1px solid #f5c45d;
  background: #fff7d6;
  color: #7c2d12;
  border-radius: 8px;
  width: 30px;
  height: 30px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;

  &:hover {
    background: #fde68a;
  }

  @media (max-width: 1024px) {
    width: 28px;
    height: 28px;
    font-size: 0.95rem;
  }
`;

const BetaModeBanner = () => {
  const location = useLocation();
  const [hidden, setHidden] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const rawValue = window.localStorage.getItem(BETA_BANNER_HIDE_KEY);
    const hideUntil = Number(rawValue || 0);
    if (Number.isFinite(hideUntil) && hideUntil > Date.now()) {
      setHidden(true);
    }
  }, []);

  useEffect(() => {
    if (!BETA_FREE_MODE) {
      return undefined;
    }
    if (hidden) {
      document.documentElement.style.setProperty('--beta-banner-height', '0px');
      return undefined;
    }
    if (!bannerRef.current) {
      return undefined;
    }

    const updateHeight = () => {
      if (!bannerRef.current) {
        return;
      }
      const height = bannerRef.current.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--beta-banner-height', `${height}px`);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(bannerRef.current);
    window.addEventListener('resize', updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
      document.documentElement.style.setProperty('--beta-banner-height', '0px');
    };
  }, [hidden]);

  if (!BETA_FREE_MODE) {
    return null;
  }

  if (hidden) {
    return null;
  }

  const isOnOffers = location.pathname.startsWith('/offers');
  const actionLabel = isOnOffers ? 'Offres' : 'Offres beta';

  const handleDismiss = () => {
    if (typeof window !== 'undefined') {
      const hideUntil = Date.now() + HIDE_DURATION_MS;
      window.localStorage.setItem(BETA_BANNER_HIDE_KEY, String(hideUntil));
    }
    setHidden(true);
  };

  return (
    <Wrapper role="status" aria-live="polite" ref={bannerRef}>
      <Inner>
        <Badge>BETA</Badge>
        <TextWrap>
          <DesktopText>
            Mode beta actif: fonctions premium ouvertes temporairement, paiements et facturation desactives. L'editeur se reserve le droit de desactiver la beta et de rendre le premium payant a tout moment.
          </DesktopText>
          <MobileText>
            Beta: premium ouvert temporairement, paiement desactive. Le premium peut redevenir payant a tout moment.
          </MobileText>
        </TextWrap>
        {!isOnOffers && <Action to="/offers">{actionLabel}</Action>}
        <CloseButton type="button" aria-label="Masquer l'information beta" onClick={handleDismiss}>
          ×
        </CloseButton>
      </Inner>
    </Wrapper>
  );
};

export default BetaModeBanner;
